import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { useState, Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import ChatWidget from "@/components/chat/ChatWidget";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/layout/ScrollToTop";
import RouteScrollToTop from "@/components/layout/RouteScrollToTop";
import InstallPrompt from "@/components/pwa/InstallPrompt";
import MobileBottomNav from "@/components/layout/MobileBottomNav";
import BackToDashboardPill from "@/components/layout/BackToDashboardPill";
import GlobalUXEnhancers from "@/components/layout/GlobalUXEnhancers";
import SuspenseFallback from "@/components/SuspenseFallback";
import MerchantKeyboardShortcuts from "@/components/layout/MerchantKeyboardShortcuts";
import CommandPalette from "@/components/layout/CommandPalette";
import ServiceWorkerUpdateBanner from "@/components/layout/ServiceWorkerUpdateBanner";
import { useUnreadTitle } from "@/hooks/useUnreadTitle";
import AppRoutes from "@/routes/AppRoutes";

function UnreadTitleBridge() {
  useUnreadTitle();
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      // Auto-refresh stale data when merchant returns to the tab
      refetchOnWindowFocus: "always",
    },
  },
});

const App = () => {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <RouteScrollToTop />
              <GlobalUXEnhancers />
              <MerchantKeyboardShortcuts />
              <CommandPalette />
              <UnreadTitleBridge />
              <ErrorBoundary>
                <Suspense fallback={<SuspenseFallback />}>
                  <AppRoutes />
                </Suspense>
              </ErrorBoundary>
              <ScrollToTop />
              <ChatWidget />
              <InstallPrompt />
              <BackToDashboardPill />
              <MobileBottomNav />
              <ServiceWorkerUpdateBanner />
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
